import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { naira } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUSES = ["Pending", "Placed", "Confirmed", "Delivered", "Cancelled"];
const STATUS_COLOR = {
  Pending: "bg-sand text-bark",
  Placed: "bg-moss/15 text-moss",
  Confirmed: "bg-terracotta/15 text-terracotta",
  Delivered: "bg-moss text-bone",
  Cancelled: "bg-destructive/15 text-destructive",
};

export default function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.put(`/admin/orders/${id}/status`, { status });
      toast.success(`Order marked ${status}`);
      setSelected(data);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-serif text-3xl text-espresso mb-1">Orders</h1>
      <p className="text-bark text-sm mb-8">{orders.length} orders</p>

      <div className="border border-sandline rounded-xl overflow-hidden">
        {/* Desktop header */}
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-sand text-bark text-xs uppercase tracking-wide font-medium">
          <span className="col-span-3">Order</span>
          <span className="col-span-3">Customer</span>
          <span className="col-span-2">Total</span>
          <span className="col-span-2">Payment</span>
          <span className="col-span-2">Status</span>
        </div>
        {orders.length === 0 && <p className="p-5 text-bark text-sm">No orders yet.</p>}
        {orders.map((o) => (
          <button
            key={o.id}
            onClick={() => setSelected(o)}
            data-testid={`order-row-${o.id}`}
            className="w-full border-t border-sandline text-left hover:bg-sand/50 first:border-t-0 md:first:border-t"
          >
            {/* Desktop row */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 items-center text-sm">
              <span className="col-span-3 text-espresso font-medium">#{o.orderNumber}</span>
              <span className="col-span-3 text-bark truncate">{o.customer?.name}</span>
              <span className="col-span-2 text-espresso">{naira(o.total)}</span>
              <span className="col-span-2 text-bark capitalize">{o.paymentStatus}</span>
              <span className="col-span-2">
                <span className={`text-xs px-2.5 py-1 rounded-full ${STATUS_COLOR[o.status] || "bg-sand"}`}>{o.status}</span>
              </span>
            </div>
            {/* Mobile card */}
            <div className="md:hidden p-4 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-espresso font-medium text-sm">#{o.orderNumber}</p>
                  <p className="text-bark text-sm truncate">{o.customer?.name}</p>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full shrink-0 ${STATUS_COLOR[o.status] || "bg-sand"}`}>{o.status}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-espresso font-medium">{naira(o.total)}</span>
                <span className="text-bark capitalize text-xs">{o.paymentStatus}</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-bone max-w-lg w-[calc(100%-1rem)] max-h-[92vh] overflow-y-auto p-5 md:p-6" data-testid="order-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-espresso">Order #{selected.orderNumber}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="bg-sand rounded-lg p-4">
                  <p className="text-espresso font-medium">{selected.customer?.name}</p>
                  <p className="text-bark">{selected.customer?.phone} · {selected.customer?.email}</p>
                  <p className="text-bark mt-1">{selected.customer?.address}, {selected.customer?.city} {selected.customer?.state}</p>
                  {selected.customer?.notes && <p className="text-bark mt-1 italic">“{selected.customer.notes}”</p>}
                </div>
                <div className="space-y-2">
                  {selected.items?.map((it, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-bark">{it.name} × {it.quantity}</span>
                      <span className="text-espresso">{naira(it.price * it.quantity)}</span>
                    </div>
                  ))}
                  <div className="border-t border-sandline pt-2 flex justify-between font-medium text-espresso">
                    <span>Total</span><span>{naira(selected.total)}</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-bark mb-1.5 block">Update status</label>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger data-testid="order-status-select" className="border-sandline"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
